import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { SimpleChartWithTableComponent } from 'app/trello/components/shared/visualisations/simple-chart-with-table/simple-chart-with-table.component';
import { GroupedMap, GroupedMap2D } from 'app/trello/models/board-summary';
import { ChartableData, LabelledValues } from 'app/trello/models/charts';
import { ReadableSecondsPipe } from 'app/trello/pipes/readable-seconds.pipe';
import { ChartOptions, ChartType, LegendOptions, TooltipOptions } from 'chart.js';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SimpleChartWithTableComponent,
  ],
  providers: [ReadableSecondsPipe],
  selector: 'app-change-chart-with-table',
  template: `
      <app-simple-chart-with-table
              [chartData]="chartData"
              [chartType]="chartType"
              [chartLegend]="chartLegend"
              [chartOptions]="chartOptions"
              [tableTitle]="tableTitle"
              [tableGroupColumnLabel]="tableGroupColumnLabel"></app-simple-chart-with-table>
  `,
})
export class ChangeChartWithTableComponent<T, U> implements OnInit {

  @Input({required: true}) chartData!:
    ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>> |
    ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>>[];

  @Input() chartLegend: boolean = true;

  @Input() horizontal: boolean = false;
  @Input() stacked: boolean = false;

  @Input() tableTitle: string | undefined;
  @Input() tableGroupColumnLabel: string = 'Group';

  @Input() valueFormat: 'number' | 'human-readable-time' | 'custom' = 'number';
  @Input() valueUnit: 'number' | 'milliseconds' | 'seconds' | 'minutes' | 'hours' | 'days' = 'number';

  @Input() valueFormatter: (value: any) => string = (value: any) => String(value);

  protected readonly chartType: ChartType = 'bar';
  protected chartOptions!: ChartOptions;

  constructor(
    private readonly readableSecondsPipe: ReadableSecondsPipe,
  ) {
  }

  public ngOnInit(): void {

    const horizontal = this.horizontal;

    const labelScale = {
      type: 'category',
      display: true,
      stacked: this.stacked,
      title: {
        display: false,
        text: '',
      },
      ticks: {
        autoSkip: true,
        maxTicksLimit: 20,
        maxRotation: 0,
        minRotation: 0,
      },
    } as any;

    const valueScale = {
      beginAtZero: true,
      stacked: this.stacked,
      title: {
        display: false,
        text: '',
      },
      ticks: {
        precision: 0,
        callback: (value: string | number) => this.formatValue(Number(value)),
      },
    } as any;

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: horizontal ? 'y' : 'x',
      interaction: {
        axis: horizontal ? 'y' : 'x',
        mode: 'index',
        intersect: false,
      },
      scales: {
        x: horizontal ? valueScale : labelScale,
        y: horizontal ? labelScale : valueScale,
      },
      plugins: {
        legend: {
          display: true,
        } as LegendOptions<'bar'>,
        tooltip: {
          position: 'nearest',
          callbacks: {
            label: (context) => {

              const label = context.dataset.label || '';

              const value =
                horizontal
                  ? context.parsed.x
                  : context.parsed.y;

              return `${label}: ${this.formatValue(value)}`;
            },
          },
        } as TooltipOptions<'bar'>,
        noTooltipColorBoxBorders: {
          enabled: true,
        },
        timeBasedTicks: {
          valueAxis: horizontal ? 'x' : 'y',
          valueUnit: this.valueUnit,
        },
      } as any,
    };
  }

  private formatValue(
    value: any,
  ): string {

    if (this.valueFormat === 'custom') {
      return this.valueFormatter(value);
    }

    if (this.valueFormat === 'human-readable-time') {

      const seconds = Number(value) / 1000;

      return this.readableSecondsPipe.transform(seconds, 1) || '';
    }

    return String(value);
  }

}
